import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  MessageCircle,
  Send,
  CheckCircle2,
  Clock,
  Globe,
  Sparkles,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '34600000000';

const Contact = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: 'Consultation inquiry',
    message: '',
    website: '', // honeypot field
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
    t(
      'contact.whatsappPrefill',
      'Hola Paola! I would love to get more information about your nutrition consultations and plans.'
    )
  )}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.website) return; // spam honeypot
    setSubmitting(true);
    setError('');
    try {
      const fullMessage = form.subject
        ? `[${form.subject}]\n\n${form.message.trim()}`
        : form.message.trim();
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: fullMessage,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || t('contact.sendError', 'Could not send your message. Please try again.'));
      }
      setSent(true);
      setForm({ name: '', email: '', subject: 'Consultation inquiry', message: '', website: '' });
    } catch (err) {
      setError(err.message || t('contact.sendError', 'Could not send your message.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/40 via-white to-sky-50/50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            {t('contact.badge', 'Get in Touch')}
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
            {t('contact.title', "Let's Start Your Balance Journey")}
          </h1>
          <p className="mt-4 text-lg text-gray-600">
            {t(
              'contact.subtitle',
              'Have a question about 1-on-1 consultations, downloadable diet plans, or collaborations? Reach out directly by email, WhatsApp, or the contact form below.'
            )}
          </p>
        </div>

        {/* Direct Contact Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
          {/* Paola direct email */}
          <a
            href="mailto:paola@todoenbalance.com"
            className="group bg-white rounded-3xl p-7 shadow-sm hover:shadow-xl border border-gray-100 transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between"
          >
            <div>
              <div className="w-12 h-12 rounded-2xl bg-blue-50 text-primary flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Mail className="w-6 h-6" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                {t('contact.directNutritionist', 'Direct to Paola')}
              </p>
              <h2 className="text-lg font-bold text-gray-900 mt-1 break-all">
                paola@todoenbalance.com
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                {t(
                  'contact.paolaEmailDesc',
                  'For personal consultation questions, clinical follow-ups, and tailored nutrition advice.'
                )}
              </p>
            </div>
            <span className="mt-6 inline-flex items-center text-sm font-bold text-primary group-hover:underline">
              {t('contact.sendEmail', 'Send an email →')}
            </span>
          </a>

          {/* General info email */}
          <a
            href="mailto:info@todoenbalance.com"
            className="group bg-white rounded-3xl p-7 shadow-sm hover:shadow-xl border border-gray-100 transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between"
          >
            <div>
              <div className="w-12 h-12 rounded-2xl bg-purple-50 text-purple-600 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <Mail className="w-6 h-6" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                {t('contact.generalInquiries', 'General & Orders')}
              </p>
              <h2 className="text-lg font-bold text-gray-900 mt-1 break-all">
                info@todoenbalance.com
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                {t(
                  'contact.infoEmailDesc',
                  'For eBook downloads, billing support, press, workshops, and brand collaborations.'
                )}
              </p>
            </div>
            <span className="mt-6 inline-flex items-center text-sm font-bold text-purple-600 group-hover:underline">
              {t('contact.emailInfo', 'Email support →')}
            </span>
          </a>

          {/* WhatsApp Chat Now */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group bg-gradient-to-br from-emerald-500 to-green-600 text-white rounded-3xl p-7 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 flex flex-col justify-between"
          >
            <div>
              <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs font-bold uppercase tracking-wider text-emerald-100">
                {t('contact.instantChat', 'Fastest Response')}
              </p>
              <h2 className="text-xl font-extrabold mt-1">
                {t('contact.whatsappTitle', 'WhatsApp Chat Now')}
              </h2>
              <p className="text-sm text-emerald-50 mt-2">
                {t(
                  'contact.whatsappDesc',
                  'Chat directly with us on WhatsApp on weekdays (9:00–18:00 CET) for quick questions before booking.'
                )}
              </p>
            </div>
            <span className="mt-6 inline-flex items-center justify-center bg-white text-emerald-700 font-bold text-sm px-5 py-3 rounded-full shadow-md group-hover:bg-emerald-50 transition-colors">
              <MessageCircle className="w-4 h-4 mr-2" />
              {t('contact.chatNowBtn', 'Start WhatsApp Chat')}
            </span>
          </a>
        </div>

        {/* Standard Contact Form + Office Info */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 sm:p-12">
          <div className="lg:col-span-2 flex flex-col justify-between space-y-8">
            <div>
              <h2 className="text-2xl font-extrabold text-gray-900">
                {t('contact.formHeading', 'Send Us a Message')}
              </h2>
              <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                {t(
                  'contact.formSubheading',
                  'Fill out the form and Paola will get back to you within 24 business hours. Available in English, Español, and Italiano.'
                )}
              </p>

              <div className="mt-8 space-y-5 text-sm">
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-gray-900">
                      {t('contact.hoursTitle', 'Consultation Hours')}
                    </p>
                    <p className="text-gray-500">
                      {t('contact.hoursDetail', 'Monday – Saturday · 09:00 – 17:00 (Europe/Madrid)')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-gray-900">
                      {t('contact.onlineWorldwide', '100% Online Worldwide')}
                    </p>
                    <p className="text-gray-500">
                      {t(
                        'contact.languagesSupported',
                        'Video consultations in Español, English & Italiano with automatic timezone conversion.'
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-gray-50 p-5 border border-gray-100 text-xs text-gray-500 space-y-1">
              <p className="font-semibold text-gray-700">
                {t('contact.directEmailsTitle', 'Direct Emails')}
              </p>
              <p>
                •{' '}
                <a href="mailto:paola@todoenbalance.com" className="text-primary hover:underline">
                  paola@todoenbalance.com
                </a>
              </p>
              <p>
                •{' '}
                <a href="mailto:info@todoenbalance.com" className="text-primary hover:underline">
                  info@todoenbalance.com
                </a>
              </p>
            </div>
          </div>

          <div className="lg:col-span-3">
            {sent ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12 px-6 bg-emerald-50/60 rounded-3xl border border-emerald-100">
                <CheckCircle2 className="w-12 h-12 text-emerald-600 mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">
                  {t('contact.sentTitle', 'Message Sent!')}
                </h3>
                <p className="text-gray-600 mt-2 max-w-md text-sm">
                  {t(
                    'contact.sentBody',
                    'Thank you for reaching out. Paola has received your message and will reply to your email shortly.'
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  className="mt-6 text-sm font-bold text-primary hover:underline"
                >
                  {t('contact.sendAnother', 'Send another message')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                    {error}
                  </div>
                )}

                {/* Honeypot */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2"
                    >
                      {t('contact.nameLabel', 'Your Name')}
                    </label>
                    <input
                      id="contact-name"
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="María García"
                      className="w-full px-4 py-3.5 rounded-2xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all text-sm"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="contact-email"
                      className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2"
                    >
                      {t('contact.emailLabel', 'Email Address')}
                    </label>
                    <input
                      id="contact-email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="maria@example.com"
                      className="w-full px-4 py-3.5 rounded-2xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="contact-subject"
                    className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2"
                  >
                    {t('contact.subjectLabel', 'Topic')}
                  </label>
                  <select
                    id="contact-subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    className="w-full px-4 py-3.5 rounded-2xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all text-sm font-medium"
                  >
                    <option value="Consultation inquiry">
                      {t('contact.topicConsultation', '1-on-1 Nutrition Consultation')}
                    </option>
                    <option value="Diet eBook / PDF support">
                      {t('contact.topicEbook', 'Diet eBook / PDF Download')}
                    </option>
                    <option value="Collaboration or press">
                      {t('contact.topicCollab', 'Collaboration / Workshop')}
                    </option>
                    <option value="General question">
                      {t('contact.topicOther', 'Other Question')}
                    </option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="contact-message"
                    className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2"
                  >
                    {t('contact.messageLabel', 'Message')}
                  </label>
                  <textarea
                    id="contact-message"
                    rows={5}
                    required
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t(
                      'contact.messagePlaceholder',
                      'Tell Paola a little about your goals or question…'
                    )}
                    className="w-full px-4 py-3.5 rounded-2xl bg-gray-50 border border-gray-200 focus:bg-white focus:ring-4 focus:ring-primary/10 focus:border-primary outline-none transition-all text-sm"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-primary disabled:bg-gray-300 text-white font-bold text-sm px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Send className="w-4 h-4" />
                  {submitting
                    ? t('contact.sending', 'Sending…')
                    : t('contact.submitBtn', 'Send Message')}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
